/* eslint-disable @typescript-eslint/no-unused-vars */
import { Warning } from '@mui/icons-material';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { Suspense, useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import CustomDialog from '@/components/Dialog/CustomDialog';
import { formatDate } from '@/utils/dateUtils';

import { getErrorMessage } from '@/utils/getErrorMessage';
import { ProjectCreatedByAvatar } from '../../Project/Components/StyledComponents';
import { stringToColor } from '@/utils/stringToColor';
import { useDeleteGenePanel, useListGenePanel } from '@/api/genes/genes';
import AppDataGrid from '@/components/DataGrid/AppDataGrid';
import CustomNoRowsOverlay from '@/components/CustomNoRowsOverlay';
import PageHeader from '@/components/PageHeader/PageHeader';
import LoadingState from '@/components/LoadingState';

const NoRowsOverlay = () => (
  <CustomNoRowsOverlay message="No gene panels available" />
);

const GenePanelLists = () => {
  const navigate = useNavigate();

  const { data, refetch, isFetching } = useListGenePanel();
  const deleteGenePanel = useDeleteGenePanel();

  // Single dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Rows (stable fallback)
  const rows = useMemo(() => data?.results ?? [], [data]);

  const askDelete = (id: string) => {
    setDeleteId(id);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteGenePanel.mutateAsync({ id: deleteId });
      enqueueSnackbar('Gene panel deleted', { variant: 'success' });
      closeDialog();
      await refetch();
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 1 },
    { field: 'version', headerName: 'Version', flex: 0.6 },
    {
      field: 'created_by',
      headerName: 'Created By',
      flex: 1,

      renderCell: (params) => {
        const { first_name: FirstName, last_name: LastName } =
          params.row.created_by || {};
        return (
          <Stack direction="row" spacing={1} marginTop="14px">
            <ProjectCreatedByAvatar
              sx={{
                backgroundColor: stringToColor(`${FirstName}${LastName}`),
              }}
            >
              {FirstName ? FirstName[0] : 'unknown'}
              {LastName ? LastName[0] : 'unknown'}
            </ProjectCreatedByAvatar>
            <Typography variant="caption">
              {FirstName} {LastName}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      flex: 0.9,
      valueGetter: (value) => {
        const readableDate = value ? formatDate(value) : '';
        return readableDate;
      },
    },
    {
      field: 'updated_at',
      headerName: 'Last Modified',
      flex: 0.9,
      valueGetter: (value) => {
        const readableDate = value ? formatDate(value) : '';
        return readableDate;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      flex: 0.8,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(`genepanel/${params.row.id}`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => askDelete(params.row.id)}
            disabled={deleteGenePanel.isPending}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{ width: '100%', maxWidth: 1680, mx: 'auto', p: { xs: 2, md: 3 } }}
    >
      <PageHeader
        eyebrow="Configuration"
        title="Gene panels"
        description="Maintain the curated gene sets available for variant prioritisation and analysis."
        actions={
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('genepanel/create')}
          >
            Create Panel
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1}
          sx={{
            px: { xs: 2, md: 2.5 },
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Box>
            <Typography variant="h3">Gene panel registry</Typography>
            <Typography variant="body2" color="text.secondary">
              Review panel versions, ownership, and recent changes.
            </Typography>
          </Box>
          <Chip
            label={`${rows.length} panel${rows.length === 1 ? '' : 's'}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Stack>
        <AppDataGrid
          rows={rows}
          columns={columns}
          loading={isFetching || deleteGenePanel.isPending}
          getRowId={(r) => r.id} // ensure id field is used
          slots={{ noRowsOverlay: NoRowsOverlay }}
          sx={{ border: 0, borderRadius: 0, minHeight: 320 }}
        />
      </Paper>

      {/* Single global dialog */}
      <CustomDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSubmit={confirmDelete}
        confirmButtonProps={{
          color: 'error',
          disabled: deleteGenePanel.isPending,
        }}
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning color="warning" />
            <span>Delete Gene Panel</span>
          </Stack>
        }
      >
        <div>Are you sure you want to delete this gene panel?</div>
      </CustomDialog>
    </Box>
  );
};

const GenePanelListsWithSuspense = () => (
  <Suspense fallback={<LoadingState label="Loading gene panels…" />}>
    <GenePanelLists />
  </Suspense>
);

export default GenePanelListsWithSuspense;
